import { Controller } from "@hotwired/stimulus"

// Hold-to-fix on the streak page (streaks/_fix). Pressing and holding the track
// for HOLD_MS fills it with fire and walks the mascot knob across, with haptics
// and shake building as it goes; letting go early springs it back. A completed
// hold posts the fix; once the server says yes the fixes feed the fire:
//
//   1. the track flashes full and the knob settles at the end
//   2. each spent token lifts out of the card and flies in an arc into the
//      hero flame, which flickers as it lands
//   3. the missed days visible in the week relight: a warm ring sweeps around
//      each dotted flame and it fills blue (a fixed day)
//   4. the fire flares: the flame swells on a spring, the heat flashes, a
//      burst of embers rises, and the number counts up to the joined run
//   5. the slider melts into a self-drawing check and "Streak repaired", the
//      spent fix section folds away, and the page reloads in place
//
// Nothing here decides what the fix is worth: the server spends the fixes and
// sends the new run back, and the animation only shows it. Lives on the page
// wrapper because the flame, the number, the week and the Current stat are
// outside the fix card. Transforms and opacity only; reduced motion skips
// straight to the end state.
const HOLD_MS = 1300
// Haptics climb as the hold goes on: a thump on press, then one per quarter.
const HOLD_STEPS = [[0.25, "medium"], [0.5, "heavy"], [0.75, "heavy"]]
const KNOB = 56

export default class extends Controller {
  static targets = ["track", "knob", "ring", "label", "shine", "body", "left", "token", "gap",
    "number", "current", "hero", "fire", "fix", "haptic", "doneTemplate"]
  static values = { url: String, leftTemplate: String, error: String }

  connect() {
    this.x = 0
    this.busy = false
    this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    // The shimmer slides across the track's width, measured once and again
    // only when the track resizes; nothing per frame.
    if (this.hasTrackTarget) {
      // entry.target, not this.trackTarget: the track is gone once the card
      // turns into "Streak repaired", and the observer still fires once.
      this.resizer = new ResizeObserver((entries) => {
        entries.forEach(({ target }) => target.style.setProperty("--fix-w", `${target.clientWidth}px`))
      })
      this.resizer.observe(this.trackTarget)
    }
  }

  disconnect() {
    this.resizer?.disconnect()
  }

  // ── Press and hold ──

  press(event) {
    if (this.busy || this.holding || this.trackTarget.classList.contains("is-disabled")) return
    event.preventDefault()
    this.trackTarget.setPointerCapture?.(event.pointerId)
    this.knobTarget.classList.remove("is-settling")
    this.max = this.trackTarget.clientWidth - KNOB
    this.dir = getComputedStyle(this.trackTarget).direction === "rtl" ? -1 : 1
    this.holding = true
    this.steps = [...HOLD_STEPS]
    this.started = Date.now()
    this.lastEmber = 0
    this.trackTarget.classList.add("is-holding")
    this.tap("medium")
    this.tick()
  }

  // Timer-driven like countUp, so a hold can never stall on a paused frame.
  tick() {
    if (!this.holding) return
    const p = Math.min(1, (Date.now() - this.started) / HOLD_MS)
    this.setProgress(p)
    while (this.steps.length && p >= this.steps[0][0]) this.tap(this.steps.shift()[1])
    if (Date.now() - this.lastEmber > 110 - p * 60) {
      this.lastEmber = Date.now()
      this.holdEmber(p)
    }
    if (p >= 1) return this.complete()
    this.timer = setTimeout(() => this.tick(), 16)
  }

  release() {
    if (!this.holding) return
    this.holding = false
    clearTimeout(this.timer)
    this.trackTarget.classList.remove("is-holding")
    this.knobTarget.classList.add("is-settling")
    this.setProgress(0)
    this.tap("light")
  }

  complete() {
    this.holding = false
    this.trackTarget.classList.remove("is-holding")
    this.tap("rigid")
    this.submit()
  }

  // A long press on iOS would otherwise raise the callout menu.
  block(event) {
    event.preventDefault()
  }

  setProgress(p) {
    const x = p * this.max
    this.trackTarget.style.setProperty("--fix-x", `${x * this.dir}px`)
    this.trackTarget.style.setProperty("--hold-shake", p.toFixed(3))
    // The fill grows from the start edge; its width never flips with direction.
    this.trackTarget.querySelector(".streak-fix-fill").style.width = `${x + KNOB}px`
    const ink = Math.max(0, 1 - p * 1.4)
    this.labelTarget.style.opacity = ink
    if (this.hasShineTarget) this.shineTarget.style.opacity = ink
    if (this.hasRingTarget) this.ringTarget.style.strokeDashoffset = 100 - p * 100
  }

  // Sparks thrown off the knob while it is held, more often as it builds.
  holdEmber(p) {
    if (this.reducedMotion) return
    const r = this.knobTarget.getBoundingClientRect()
    const ember = document.createElement("i")
    ember.className = "streak-fix-hold-ember"
    ember.style.left = `${r.left + r.width / 2}px`
    ember.style.top = `${r.top + r.height / 2}px`
    document.body.appendChild(ember)
    const drift = (Math.random() - 0.5) * 40 - 14 * this.dir
    const rise = 26 + Math.random() * 30 + p * 30
    ember.animate([
      { transform: "translate(-50%, -50%) scale(1)", opacity: 1 },
      { transform: `translate(calc(-50% + ${drift}px), calc(-50% - ${rise}px)) scale(.3)`, opacity: 0 }
    ], { duration: 520 + Math.random() * 260, easing: "cubic-bezier(.2, .7, .3, 1)", fill: "forwards" })
    setTimeout(() => ember.remove(), 820)
  }

  // ── Server ──

  async submit() {
    if (this.busy) return
    this.busy = true
    this.tap("light")
    let data = null
    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      const response = await fetch(this.urlValue, {
        method: "POST",
        credentials: "same-origin",
        headers: { "X-CSRF-Token": token, Accept: "application/json" }
      })
      if (response.ok) data = await response.json()
    } catch {
      data = null
    }

    if (!data) return this.fail()
    await this.repair(data)
  }

  fail() {
    this.busy = false
    this.labelTarget.textContent = this.errorValue
    if (this.hasShineTarget) this.shineTarget.remove()
    this.knobTarget.classList.add("is-settling")
    this.setProgress(0)
  }

  // ── Repair ──

  async repair({ current_streak: run, fixes_left: left }) {
    // The dashboard behind this screen still shows the broken streak; drop
    // the snapshot so going back fetches it fresh.
    window.Turbo?.cache?.clear()
    const from = parseInt(this.numberTarget.textContent, 10) || 0

    if (this.reducedMotion) {
      this.numberTarget.textContent = run
      if (this.hasCurrentTarget) this.currentTarget.textContent = run
      this.mirrorCount(run)
      return this.reload(300)
    }

    this.trackTarget.classList.add("is-lit")
    await this.bringFireIntoView()
    await this.feedFire()
    for (const day of this.gapTargets) await this.repairDay(day)

    this.tap("success")
    this.flare()
    await Promise.all([
      this.countUp(this.numberTarget, from, run, 1100),
      this.hasCurrentTarget ? this.countUp(this.currentTarget, from, run, 1100) : null
    ])
    this.mirrorCount(run)

    this.leftTarget.textContent = this.leftTemplateValue.replace("__LEFT__", left)
    await this.resolveCard()
    await this.foldAway()
    this.reload(250)
  }

  // Same replace visit the due deck uses, so the page comes back as the
  // server's truth (fixed days in every week, the fix card gone).
  async reload(ms) {
    await this.wait(ms)
    window.Turbo.visit(window.location.href, { action: "replace" })
  }

  // The flame is above the card; on a short screen it can be scrolled away.
  async bringFireIntoView() {
    const r = this.fireTarget.getBoundingClientRect()
    if (r.top >= 0 && r.bottom <= window.innerHeight) return
    this.fireTarget.scrollIntoView({ behavior: "smooth", block: "center" })
    await this.wait(420)
  }

  // Each spent token flies in an arc into the flame and is swallowed by it.
  async feedFire() {
    const tokens = [...this.tokenTargets].reverse()
    for (const token of tokens) {
      token.classList.remove("is-armed")
      this.fly(token)
      this.tap("light")
      await this.wait(170)
    }
    await this.wait(560)
  }

  fly(token) {
    const from = token.getBoundingClientRect()
    const to = this.fireTarget.getBoundingClientRect()
    const ghost = token.cloneNode(true)
    ghost.removeAttribute("data-streak-fix-target")
    ghost.classList.add("streak-fix-ghost")
    Object.assign(ghost.style, { left: `${from.left}px`, top: `${from.top}px`, width: `${from.width}px`, height: `${from.height}px` })
    document.body.appendChild(ghost)
    token.classList.add("is-used")

    const dx = to.left + to.width / 2 - (from.left + from.width / 2)
    const dy = to.top + to.height * 0.62 - (from.top + from.height / 2)
    const lift = Math.min(90, Math.abs(dy) * 0.35)
    const flight = ghost.animate([
      { transform: "translate(0, 0) scale(1)", opacity: 1 },
      { transform: `translate(${dx * 0.45}px, ${dy * 0.55 - lift}px) scale(1.9)`, opacity: 1, offset: 0.55 },
      { transform: `translate(${dx}px, ${dy}px) scale(.5)`, opacity: 0 }
    ], { duration: 640, easing: "cubic-bezier(.45, 0, .2, 1)", fill: "forwards" })
    flight.onfinish = () => {
      ghost.remove()
      this.flicker()
    }
  }

  // A small gulp each time a token lands.
  flicker() {
    this.fireTarget.classList.remove("is-fed")
    void this.fireTarget.offsetWidth
    this.fireTarget.classList.add("is-fed")
  }

  // The payoff: the flame swells, the heat flashes, embers burst upward.
  flare() {
    this.fireTarget.classList.add("is-flaring")
    const r = this.fireTarget.getBoundingClientRect()
    const cx = r.left + r.width / 2
    const top = r.top + r.height * 0.3
    for (let k = 0; k < 16; k++) {
      const ember = document.createElement("i")
      ember.className = "streak-fix-ember"
      ember.style.left = `${cx + (Math.random() - 0.5) * r.width * 0.5}px`
      ember.style.top = `${top + Math.random() * r.height * 0.4}px`
      document.body.appendChild(ember)
      const drift = (Math.random() - 0.5) * 120
      const rise = 90 + Math.random() * 110
      ember.animate([
        { transform: "translate(-50%, -50%) scale(1)", opacity: 0 },
        { opacity: 1, offset: 0.12 },
        { transform: `translate(calc(-50% + ${drift}px), calc(-50% - ${rise}px)) scale(.3)`, opacity: 0 }
      ], { duration: 900 + Math.random() * 700, delay: Math.random() * 180, easing: "cubic-bezier(.2, .7, .3, 1)", fill: "both" })
      setTimeout(() => ember.remove(), 1900)
    }
  }

  async repairDay(circle) {
    circle.insertAdjacentHTML("beforeend",
      '<svg class="streak-fix-ring" viewBox="0 0 40 40" aria-hidden="true"><circle cx="20" cy="20" r="18"/></svg>')
    await this.wait(520)
    circle.querySelector(".streak-fix-ring")?.remove()
    circle.classList.remove("is-missed", "is-gap")
    circle.classList.add("is-fixed", "is-repairing")
    this.sparks(circle)
    this.tap("light")
    await this.wait(160)
  }

  sparks(el) {
    const r = el.getBoundingClientRect()
    const cx = r.left + r.width / 2
    const cy = r.top + r.height / 2
    for (let k = 0; k < 8; k++) {
      const spark = document.createElement("i")
      spark.className = "streak-fix-spark"
      spark.style.left = `${cx}px`
      spark.style.top = `${cy}px`
      document.body.appendChild(spark)
      const a = (k / 8) * Math.PI * 2
      spark.animate([
        { transform: "translate(-50%, -50%)", opacity: 1 },
        { transform: `translate(-50%, -50%) translate(${Math.cos(a) * 26}px, ${Math.sin(a) * 26}px) scale(.3)`, opacity: 0 }
      ], { duration: 520, easing: "cubic-bezier(.2, .8, .3, 1)", fill: "forwards" })
      setTimeout(() => spark.remove(), 560)
    }
  }

  // Ticks through every number on the way, easing out, so the run visibly
  // climbs back rather than swapping. Timer-driven, not requestAnimationFrame:
  // rAF stops while the web view is not painting, and a stalled count would
  // strand the page half-repaired with no reload.
  countUp(el, from, to, ms) {
    const t0 = Date.now()
    return new Promise((resolve) => {
      const step = () => {
        const p = Math.min(1, (Date.now() - t0) / ms)
        const eased = 1 - Math.pow(1 - p, 4)
        el.textContent = Math.round(from + (to - from) * eased)
        if (p < 1) setTimeout(step, 16)
        else resolve()
      }
      step()
    })
  }

  // The slider melts into a check that draws itself, then the words.
  async resolveCard() {
    this.bodyTarget.classList.add("is-out")
    await this.wait(240)
    this.bodyTarget.innerHTML = this.doneTemplateTarget.innerHTML
    this.bodyTarget.classList.remove("is-out")
    this.bodyTarget.querySelector(".streak-fix-done")?.classList.add("is-drawing")
    await this.wait(1100)
  }

  // The spent section folds shut: its height eases to zero as it fades.
  async foldAway() {
    if (!this.hasFixTarget) return
    const el = this.fixTarget
    el.style.height = `${el.offsetHeight}px`
    void el.offsetHeight
    el.classList.add("is-folding")
    el.style.height = "0px"
    await this.wait(480)
  }

  // The native streak chip and the hero's stored count follow the new run.
  mirrorCount(run) {
    const counter = this.application.getControllerForElementAndIdentifier(this.heroTarget, "streak-count")
    counter?.update?.(run)
  }

  // style is one HapticComponent.swift knows: light, medium, success, ...
  tap(style) {
    const haptic = this.application.getControllerForElementAndIdentifier(this.hapticTarget, "bridge--haptic")
    if (!haptic) return
    haptic.styleValue = style
    haptic.tap()
  }

  wait(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }
}
